<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import {
  state,
  getSource,
  activeDocument,
  snippetsForActive,
  setZoom,
  setMode,
  commentsForActivePage,
  addPageComment,
  updatePageComment,
  removePageComment,
} from '../lib/store'
import { auth } from '../lib/auth'
import {
  extractRegionText,
  extractRegionTable,
  extractTextFromBoxes,
  extractTableFromBoxes,
  renderRegionForOcr,
  renderImageRegionForOcr,
  cropFromCanvas,
  cropCanvas,
  type CanvasRect,
} from '../lib/extraction'
import { ocr } from '../lib/ocr'
import { commitSnip } from '../lib/snip'
import type { NormRect, PageComment, SnipMode } from '../types'
import type { TextContent } from 'pdfjs-dist/types/src/display/api'
import type { PageViewport } from 'pdfjs-dist'
import type { PdfPage } from '../lib/pdf'
import type { DocxPage } from '../lib/docx'

const canvasRef = ref<HTMLCanvasElement | null>(null)
const wrapRef = ref<HTMLDivElement | null>(null)

const canvasW = ref(0)
const canvasH = ref(0)

let textContent: TextContent | null = null
let viewport: PageViewport | null = null
let curScale = 1
let isPdf = false
let currentPage: PdfPage | null = null // active PDF.js page, for high-DPI OCR re-rendering
let currentDocxPage: DocxPage | null = null // active rendered .docx page (raster + text layer)
let renderToken = 0
// In-flight PDF.js render. Must be cancelled before starting another render on the same canvas,
// otherwise PDF.js throws "Cannot use the same canvas during multiple render operations" and the
// page goes blank (happens when switching documents/pages quickly).
let currentRenderTask: { cancel: () => void; promise: Promise<void> } | null = null
// Device pixel ratio used for the last render. The canvas is rendered at CSS-size × dpr
// for crisp output on HiDPI/retina screens, but displayed (and all coordinates measured)
// at CSS size — so selection/extraction math stays in CSS space.
let renderDpr = 1

// Active drag selection (in canvas px)
const dragging = ref(false)
const sel = ref<CanvasRect | null>(null)

const pageSnips = computed(() =>
  snippetsForActive().filter(
    (s) => s.pageNumber === state.activePage && (s.rotation ?? 0) === state.rotation,
  ),
)

// ---- Positional comments ----
const pageComments = computed(() =>
  commentsForActivePage().filter((c) => (c.rotation ?? 0) === state.rotation),
)
const editingCommentId = ref<string | null>(null)
const commentDraft = ref('')
const activeComment = computed(() => pageComments.value.find((c) => c.id === editingCommentId.value) ?? null)

function openComment(c: PageComment) {
  editingCommentId.value = c.id
  commentDraft.value = c.text
}
function saveComment() {
  if (editingCommentId.value) {
    const text = commentDraft.value.trim()
    if (text) updatePageComment(editingCommentId.value, text)
    else removePageComment(editingCommentId.value)
  }
  editingCommentId.value = null
}
function deleteComment() {
  if (editingCommentId.value) removePageComment(editingCommentId.value)
  editingCommentId.value = null
}
function commentStyle(c: PageComment) {
  return { left: c.x * canvasW.value + 'px', top: c.y * canvasH.value + 'px' }
}

async function render(): Promise<void> {
  const doc = activeDocument()
  const canvas = canvasRef.value
  if (!doc || !canvas) return
  const src = getSource(doc.id)
  if (!src) {
    // Document metadata persisted but binary not re-imported this session.
    canvasW.value = 0
    canvasH.value = 0
    return
  }

  const token = ++renderToken
  const ctx = canvas.getContext('2d')!
  const dpr = Math.min(window.devicePixelRatio || 1, 3)
  renderDpr = dpr

  if (src.kind === 'pdf' && src.pdf) {
    const page = await src.pdf.getPage(state.activePage)
    if (token !== renderToken) return
    // Cancel any render still running on this canvas before reconfiguring it.
    currentRenderTask?.cancel()
    // Display viewport (CSS px, used for coordinates/extraction) vs render viewport (hi-dpr).
    // Rotation is baked into the viewport transform, so extraction maps correctly at any angle.
    const vp = page.getViewport({ scale: state.zoom, rotation: state.rotation })
    const vpRender = page.getViewport({ scale: state.zoom * dpr, rotation: state.rotation })
    canvas.width = Math.ceil(vpRender.width)
    canvas.height = Math.ceil(vpRender.height)
    canvas.style.width = `${Math.ceil(vp.width)}px`
    canvas.style.height = `${Math.ceil(vp.height)}px`
    const task = page.render({ canvasContext: ctx, viewport: vpRender })
    currentRenderTask = task
    try {
      await task.promise
    } catch {
      return // render was cancelled/superseded by a newer one
    } finally {
      if (currentRenderTask === task) currentRenderTask = null
    }
    if (token !== renderToken) return
    textContent = await page.getTextContent()
    viewport = vp
    curScale = state.zoom
    isPdf = true
    currentPage = page
    currentDocxPage = null
    canvasW.value = Math.ceil(vp.width)
    canvasH.value = Math.ceil(vp.height)
  } else if (src.kind === 'docx' && src.pages && src.pages.length) {
    const page = src.pages[Math.min(state.activePage, src.pages.length) - 1]
    currentDocxPage = page
    const rot = state.rotation
    const baseW = Math.max(1, Math.round(page.width * state.zoom))
    const baseH = Math.max(1, Math.round(page.height * state.zoom))
    const swap = rot === 90 || rot === 270
    const w = swap ? baseH : baseW
    const h = swap ? baseW : baseH
    canvas.width = Math.round(w * dpr)
    canvas.height = Math.round(h * dpr)
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.save()
    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.rotate((rot * Math.PI) / 180)
    ctx.drawImage(page.canvas, (-baseW * dpr) / 2, (-baseH * dpr) / 2, baseW * dpr, baseH * dpr)
    ctx.restore()
    textContent = null
    viewport = null
    isPdf = false
    currentPage = null
    canvasW.value = w
    canvasH.value = h
  } else if (src.kind === 'image' && src.image) {
    const img = src.image
    const rot = state.rotation
    const baseW = Math.max(1, Math.round(img.naturalWidth * state.zoom))
    const baseH = Math.max(1, Math.round(img.naturalHeight * state.zoom))
    const swap = rot === 90 || rot === 270
    const w = swap ? baseH : baseW // displayed (rotated) CSS size
    const h = swap ? baseW : baseH
    canvas.width = Math.round(w * dpr)
    canvas.height = Math.round(h * dpr)
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.save()
    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.rotate((rot * Math.PI) / 180)
    ctx.drawImage(img, (-baseW * dpr) / 2, (-baseH * dpr) / 2, baseW * dpr, baseH * dpr)
    ctx.restore()
    textContent = null
    viewport = null
    isPdf = false
    currentPage = null
    currentDocxPage = null
    canvasW.value = w
    canvasH.value = h
  }
}

onMounted(render)
watch(() => [state.activeDocId, state.activePage, state.zoom, state.rotation, state.sourcesVersion], render)

function posIn(e: PointerEvent): { x: number; y: number } {
  const r = wrapRef.value!.getBoundingClientRect()
  return {
    x: Math.min(Math.max(0, e.clientX - r.left), canvasW.value),
    y: Math.min(Math.max(0, e.clientY - r.top), canvasH.value),
  }
}

let startPt = { x: 0, y: 0 }

function onDown(e: PointerEvent): void {
  if (state.busy) return
  // In comment mode a click drops a comment pin instead of starting a snip selection.
  if (state.commentMode) {
    const p = posIn(e)
    const author = auth.user?.displayName || auth.user?.email || 'You'
    const c = addPageComment(p.x / canvasW.value, p.y / canvasH.value, author)
    editingCommentId.value = c.id
    commentDraft.value = ''
    return
  }
  ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
  dragging.value = true
  startPt = posIn(e)
  sel.value = { left: startPt.x, top: startPt.y, width: 0, height: 0 }
}

function onMove(e: PointerEvent): void {
  if (!dragging.value) return
  const p = posIn(e)
  sel.value = {
    left: Math.min(startPt.x, p.x),
    top: Math.min(startPt.y, p.y),
    width: Math.abs(p.x - startPt.x),
    height: Math.abs(p.y - startPt.y),
  }
}

async function onUp(): Promise<void> {
  if (!dragging.value) return
  dragging.value = false
  const rect = sel.value
  sel.value = null
  if (!rect || rect.width < 6 || rect.height < 6) return
  await resolveAndSnip(rect)
}

async function resolveAndSnip(rect: CanvasRect): Promise<void> {
  let text = ''
  let source: 'text-layer' | 'ocr' = 'text-layer'

  const norm: NormRect = {
    x: rect.left / canvasW.value,
    y: rect.top / canvasH.value,
    w: rect.width / canvasW.value,
    h: rect.height / canvasH.value,
  }

  if (isPdf && textContent && viewport) {
    // Table mode uses the spatial column model; other modes use line-flow extraction.
    text =
      state.mode === 'table'
        ? extractRegionTable(textContent, viewport, curScale, rect)
        : extractRegionText(textContent, viewport, curScale, rect)
  } else if (currentDocxPage && state.rotation === 0) {
    // DOCX text layer: boxes are in zoom-1 page space, so scale the selection back by 1/zoom.
    // (When the page is rotated we skip the layer and let OCR handle it.)
    const z = state.zoom || 1
    const pageSel = {
      left: rect.left / z,
      top: rect.top / z,
      width: rect.width / z,
      height: rect.height / z,
    }
    text =
      state.mode === 'table'
        ? extractTableFromBoxes(currentDocxPage.boxes, pageSel)
        : extractTextFromBoxes(currentDocxPage.boxes, pageSel)
  }
  if (!text.trim()) {
    state.busy = true
    state.status = 'Running OCR…'
    try {
      const crop = await ocrCropFor(norm, rect)
      text = await ocr(crop)
      source = 'ocr'
    } finally {
      state.busy = false
    }
  }

  await commitSnip({ text, source, rect: norm, pageNumber: state.activePage })
}

/**
 * Produce the bitmap fed to OCR. PDFs are re-rendered from the page at a fixed ~300 DPI
 * (resolution-independent of the current zoom); images are cropped at native resolution;
 * anything else falls back to sampling the on-screen canvas.
 */
async function ocrCropFor(norm: NormRect, rect: CanvasRect): Promise<HTMLCanvasElement> {
  const doc = activeDocument()
  const src = doc ? getSource(doc.id) : undefined
  if (isPdf && currentPage) {
    return renderRegionForOcr(currentPage, norm, state.rotation)
  }
  if (src?.kind === 'docx' && currentDocxPage && state.rotation === 0) {
    // The page raster is already high-res; crop the region directly (rotation 0 only).
    return cropFromCanvas(currentDocxPage.canvas, norm)
  }
  if (src?.kind === 'image' && src.image) {
    return renderImageRegionForOcr(src.image, norm, state.rotation)
  }
  // Fallback: crop the rendered canvas (CSS-space selection → canvas pixels).
  const pxRect = {
    left: rect.left * renderDpr,
    top: rect.top * renderDpr,
    width: rect.width * renderDpr,
    height: rect.height * renderDpr,
  }
  return cropCanvas(canvasRef.value!, pxRect, renderDpr >= 2 ? 1 : 2)
}

// Ctrl + mouse-wheel zooms (matches the original); plain wheel scrolls normally.
function onWheel(e: WheelEvent): void {
  if (!e.ctrlKey) return
  e.preventDefault()
  const delta = e.deltaY < 0 ? 0.1 : -0.1
  setZoom(Math.round((state.zoom + delta) * 100) / 100)
}

// Right-click → pick a snip mode (matches the original page context menu).
const ctxMenu = ref<{ x: number; y: number } | null>(null)
const CTX_MODES: { key: SnipMode; label: string }[] = [
  { key: 'text', label: 'Text' },
  { key: 'sum', label: 'Sum' },
  { key: 'table', label: 'Table' },
  { key: 'validation', label: 'Validation' },
  { key: 'exception', label: 'Exception' },
]
function onContextMenu(e: MouseEvent): void {
  e.preventDefault()
  ctxMenu.value = { x: e.clientX, y: e.clientY }
}
function chooseMode(m: SnipMode): void {
  setMode(m)
  ctxMenu.value = null
}

// Keyboard: Escape cancels a selection / menu; 1–5 switch modes. Ignored while typing.
const MODE_KEYS: Record<string, SnipMode> = {
  '1': 'text',
  '2': 'sum',
  '3': 'table',
  '4': 'validation',
  '5': 'exception',
}
function onKey(e: KeyboardEvent): void {
  const t = e.target as HTMLElement | null
  if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return
  if (e.key === 'Escape') {
    sel.value = null
    dragging.value = false
    ctxMenu.value = null
    editingCommentId.value = null
    return
  }
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g') {
    e.preventDefault()
    state.commentMode = !state.commentMode
    return
  }
  if (MODE_KEYS[e.key]) setMode(MODE_KEYS[e.key])
}
onMounted(() => window.addEventListener('keydown', onKey))

function rectStyle(r: NormRect) {
  return {
    left: r.x * canvasW.value + 'px',
    top: r.y * canvasH.value + 'px',
    width: r.w * canvasW.value + 'px',
    height: r.h * canvasH.value + 'px',
  }
}

const missingBinary = computed(() => {
  void state.sourcesVersion // re-evaluate when embedded sources finish restoring
  const doc = activeDocument()
  return !!doc && !getSource(doc.id)
})
</script>

<template>
  <div class="viewer scroll" @wheel="onWheel">
    <div v-if="missingBinary" class="reimport">
      <p>This document’s snippets were restored from the workbook, but the source file
        isn’t loaded in this session.</p>
      <p class="muted">Re-import “{{ activeDocument()?.name }}” to view its pages again.
        Existing cell links still work.</p>
    </div>

    <div
      v-else
      ref="wrapRef"
      class="page-wrap"
      :style="{ width: canvasW + 'px', height: canvasH + 'px' }"
      @pointerdown="onDown"
      @pointermove="onMove"
      @pointerup="onUp"
      @pointercancel="onUp"
      @contextmenu="onContextMenu"
    >
      <canvas ref="canvasRef" class="page-canvas"></canvas>
      <div class="sel-layer">
        <div
          v-for="s in pageSnips"
          :key="s.id"
          class="snip-rect"
          :class="['mode-' + s.mode, { highlight: s.id === state.highlightedSnippetId }]"
          :style="rectStyle(s.rect)"
          :title="s.displayName + ' → ' + s.cellAddress"
        ></div>
        <div v-if="sel" class="active-sel" :style="rectStyle({ x: sel.left / canvasW, y: sel.top / canvasH, w: sel.width / canvasW, h: sel.height / canvasH })"></div>

        <template v-if="state.showComments">
          <button
            v-for="c in pageComments"
            :key="c.id"
            class="cmt-pin"
            :class="{ open: c.id === editingCommentId }"
            :style="commentStyle(c)"
            :title="c.text || '(empty comment)'"
            @pointerdown.stop
            @click.stop="openComment(c)"
          >💬</button>
        </template>
        <div v-if="activeComment" class="cmt-pop" :style="commentStyle(activeComment)" @pointerdown.stop>
          <textarea v-model="commentDraft" placeholder="Add a comment…"></textarea>
          <div class="cmt-actions">
            <span class="who muted">{{ activeComment.author }}</span>
            <button class="del" @click="deleteComment">Delete</button>
            <button class="primary sv" @click="saveComment">Save</button>
          </div>
        </div>
      </div>
    </div>

    <ul v-if="ctxMenu" class="ctx" :style="{ left: ctxMenu.x + 'px', top: ctxMenu.y + 'px' }">
      <li
        v-for="m in CTX_MODES"
        :key="m.key"
        :class="{ on: state.mode === m.key }"
        @click="chooseMode(m.key)"
      >
        {{ m.label }}
      </li>
    </ul>
    <div
      v-if="ctxMenu"
      class="ctx-backdrop"
      @pointerdown="ctxMenu = null"
      @contextmenu.prevent="ctxMenu = null"
    ></div>
  </div>
</template>

<style scoped>
.viewer {
  flex: 1;
  background: var(--bg-soft);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 10px;
}
.page-wrap {
  position: relative;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.18);
  background: #fff;
  cursor: crosshair;
  touch-action: none;
  user-select: none;
  flex: none;
}
.page-canvas {
  display: block;
}
.sel-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.snip-rect {
  position: absolute;
  border: 1.5px solid var(--brand);
  background: rgba(43, 108, 176, 0.12);
  border-radius: 2px;
}
.snip-rect.mode-sum {
  border-color: #c97b30;
  background: rgba(253, 233, 217, 0.4);
}
.snip-rect.mode-table {
  border-color: #7b5ea7;
}
.snip-rect.mode-validation {
  border-color: #5a8a32;
}
.snip-rect.mode-exception {
  border-color: var(--danger);
}
.snip-rect.highlight {
  border-width: 2.5px;
  box-shadow: 0 0 0 3px rgba(43, 108, 176, 0.35);
  animation: snip-pulse 1s ease-in-out 2;
}
@keyframes snip-pulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(43, 108, 176, 0.25); }
  50% { box-shadow: 0 0 0 6px rgba(43, 108, 176, 0.5); }
}
.active-sel {
  position: absolute;
  border: 1.5px dashed var(--brand-dark);
  background: rgba(43, 108, 176, 0.18);
}
.reimport {
  max-width: 320px;
  margin: 32px auto;
  text-align: center;
  color: var(--ink);
}
.ctx {
  position: fixed;
  z-index: 60;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  min-width: 120px;
}
.ctx li {
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}
.ctx li:hover { background: var(--brand-50); }
.ctx li.on { background: var(--brand); color: #fff; }
.ctx-backdrop {
  position: fixed;
  inset: 0;
  z-index: 55;
}
.cmt-pin {
  position: absolute;
  transform: translate(-50%, -100%);
  pointer-events: auto;
  border: none;
  background: #fff3bf;
  border: 1px solid #f0c000;
  border-radius: 50% 50% 50% 0;
  width: 22px;
  height: 22px;
  font-size: 11px;
  line-height: 1;
  display: grid;
  place-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}
.cmt-pin.open { outline: 2px solid var(--brand); }
.cmt-pop {
  position: absolute;
  transform: translate(-50%, 8px);
  pointer-events: auto;
  z-index: 40;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  padding: 6px;
  width: 180px;
}
.cmt-pop textarea {
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 4px;
  font: inherit;
  font-size: 12px;
  resize: vertical;
  min-height: 44px;
}
.cmt-actions { display: flex; align-items: center; gap: 4px; margin-top: 4px; }
.cmt-actions .who { flex: 1; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cmt-actions button { padding: 2px 8px; font-size: 11px; }
.cmt-actions .del:hover { color: var(--danger); border-color: var(--danger); }
</style>
